import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Lightning, Plus, Trash, Play, Clock, CheckCircle,
  XCircle, ArrowRight, FlowArrow, Copy, Robot} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listWorkflows, createWorkflow, updateWorkflow, deleteWorkflow, toggleWorkflow,
  testWorkflow, getWorkflowHistory, getWorkflowTemplates, getWorkflowSchema,
  getRecentExecutions
} from '../services/api';
import WorkflowBuilder from '../components/WorkflowBuilder';
import { PageHero } from '../components/motion/PageHero';

const TRIGGER_ICONS = {
  entity_status_change: '🔄',
  entity_created: '✨',
  payment_received: '💰',
  dossier_validated: '✅',
  task_completed: '☑️',
};

const ACTION_COLORS = {
  create_task: 'bg-blue-100 text-blue-700',
  send_notification: 'bg-amber-100 text-amber-700',
  update_field: 'bg-purple-100 text-purple-700',
  create_activity: 'bg-emerald-100 text-emerald-700',
  send_email: 'bg-pink-100 text-pink-700',
  send_sms: 'bg-cyan-100 text-cyan-700',
  delay: 'bg-stone-100 text-stone-700',
  webhook_http: 'bg-indigo-100 text-indigo-700',
  condition_branch: 'bg-orange-100 text-orange-700',
};

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyWfName, setHistoryWfName] = useState('');
  const [recentExecs, setRecentExecs] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [builderData, setBuilderData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wfRes, tplRes, schemaRes] = await Promise.all([
        listWorkflows(), getWorkflowTemplates(), getWorkflowSchema()
      ]);
      setWorkflows(wfRes.data.items || []);
      setTemplates(tplRes.data.templates || []);
      setSchema(schemaRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (id) => {
    try {
      const res = await toggleWorkflow(id);
      setWorkflows(prev => prev.map(w => w.id === id ? { ...w, is_active: res.data.is_active } : w));
      toast.success(res.data.is_active ? 'Workflow active' : 'Workflow desactive');
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteWorkflow(id);
      setWorkflows(prev => prev.filter(w => w.id !== id));
      toast.success('Workflow supprime');
    } catch { toast.error('Erreur'); }
  };

  const handleTest = async (id) => {
    try {
      toast.info('Test en cours...');
      const res = await testWorkflow(id);
      if (res.data.success) {
        toast.success(`Test reussi: ${res.data.actions?.length || 0} action(s) executee(s)`);
      } else {
        toast.error(res.data.message || 'Conditions non remplies');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur test');
    }
  };

  const handleViewHistory = async (wfId, name) => {
    try {
      const res = await getWorkflowHistory(wfId);
      setHistoryData(res.data.items || []);
      setHistoryWfName(name);
      setHistoryOpen(true);
    } catch { toast.error('Erreur chargement historique'); }
  };

  const handleShowRecent = async () => {
    try {
      const res = await getRecentExecutions();
      setRecentExecs(res.data.items || []);
      setShowRecent(true);
    } catch { toast.error('Erreur'); }
  };

  const resetBuilder = () => {
    setBuilderData(null);
    setCreateOpen(false);
  };

  const openFromTemplate = (tpl) => {
    setBuilderData({ name: tpl.name, description: tpl.description, trigger: tpl.trigger, conditions: [], actions: tpl.actions });
    setCreateOpen(true);
  };

  const handleBuilderSave = async (payload) => {
    if (!payload.name?.trim()) { toast.error('Nom requis'); return; }
    if (!payload.trigger?.type) { toast.error('Trigger requis'); return; }
    if (!payload.actions?.length) { toast.error('Au moins une action requise'); return; }
    setSaving(true);
    try {
      if (builderData?.id) {
        await updateWorkflow(builderData.id, payload);
        toast.success('Workflow mis a jour !');
      } else {
        await createWorkflow(payload);
        toast.success('Workflow cree !');
      }
      setCreateOpen(false);
      setBuilderData(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleEdit = async (wf) => {
    setBuilderData(wf);
    setCreateOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Automatisations"
        subtitle="Workflows, scénarios et déclencheurs métiers"
        icon={<Robot size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#00AEEF', '#D5007D']}
      />

      {/* Templates */}
      {templates.length > 0 && workflows.length === 0 && (
        <div data-testid="workflow-templates">
          <h2 className="text-sm font-semibold text-[#1A2421] mb-3">Templates disponibles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {templates.map(tpl => (
              <Card key={tpl.id} className="border-[#E8E6E1] cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" onClick={() => openFromTemplate(tpl)} data-testid={`template-${tpl.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center flex-shrink-0">
                      <Lightning size={18} className="text-[#2D5A4C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-[#1A2421] truncate">{tpl.name}</h3>
                      <p className="text-xs text-[#5E6D66] mt-0.5 line-clamp-2">{tpl.description}</p>
                      <div className="flex gap-1 mt-2">
                        {tpl.actions.map((a, i) => (
                          <Badge key={i} className={`text-[9px] ${ACTION_COLORS[a.type] || 'bg-gray-100 text-gray-600'}`}>
                            {schema?.actions?.[a.type]?.label || a.type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Copy size={14} className="text-[#5E6D66] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Workflow List */}
      {workflows.length === 0 && templates.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-16 text-center">
            <FlowArrow size={40} className="mx-auto text-[#E8E6E1] mb-3" />
            <p className="text-[#5E6D66]">Aucun workflow configure</p>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="mt-3 bg-[#2D5A4C] hover:bg-[#1E4136]">
              <Plus size={14} className="mr-1" /> Creer mon premier workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="workflows-list">
          {workflows.map(wf => (
            <Card key={wf.id} className={`border-[#E8E6E1] transition-all duration-200 ${!wf.is_active ? 'opacity-60' : ''}`} data-testid={`workflow-${wf.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Status toggle */}
                  <div className="pt-0.5">
                    <Switch checked={wf.is_active} onCheckedChange={() => handleToggle(wf.id)} data-testid={`toggle-${wf.id}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-[#1A2421] truncate">{wf.name}</h3>
                      <Badge className={wf.is_active ? 'bg-emerald-100 text-emerald-700 text-[9px]' : 'bg-gray-100 text-gray-500 text-[9px]'}>
                        {wf.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                    {wf.description && <p className="text-xs text-[#5E6D66] mb-2">{wf.description}</p>}

                    {/* Flow visualization */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className="bg-[#2D5A4C]/10 text-[#2D5A4C] text-[9px]">
                        {TRIGGER_ICONS[wf.trigger?.type] || '⚡'} {schema?.triggers?.[wf.trigger?.type]?.label || wf.trigger?.type}
                      </Badge>
                      <ArrowRight size={10} className="text-[#E8E6E1]" />
                      {wf.conditions && (
                        (() => {
                          const condCount = Array.isArray(wf.conditions) ? wf.conditions.length
                            : typeof wf.conditions === 'object' && wf.conditions.groups
                            ? wf.conditions.groups.reduce((s, g) => s + (g.rules || []).length, 0)
                            : 0;
                          return condCount > 0 ? (
                            <>
                              <Badge className="bg-amber-50 text-amber-600 text-[9px]">{condCount} condition(s)</Badge>
                              <ArrowRight size={10} className="text-[#E8E6E1]" />
                            </>
                          ) : null;
                        })()
                      )}
                      {(wf.actions || []).map((a, i) => (
                        <Badge key={i} className={`text-[9px] ${ACTION_COLORS[a.type] || 'bg-gray-100 text-gray-600'}`}>
                          {schema?.actions?.[a.type]?.label || a.type}
                        </Badge>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-2 text-[10px] text-[#5E6D66]">
                      <span>Declenche {wf.trigger_count || 0} fois</span>
                      {wf.last_triggered_at && (
                        <span>Dernier: {new Date(wf.last_triggered_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                      )}
                      <span>Cree par {wf.created_by_name || wf.created_by}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(wf)} title="Modifier" data-testid={`edit-${wf.id}`}>
                      <Lightning size={14} className="text-[#5E6D66]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleTest(wf.id)} title="Tester" data-testid={`test-${wf.id}`}>
                      <Play size={14} className="text-[#2D5A4C]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewHistory(wf.id, wf.name)} title="Historique" data-testid={`history-${wf.id}`}>
                      <Clock size={14} className="text-[#5E6D66]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(wf.id)} title="Supprimer" data-testid={`delete-${wf.id}`}>
                      <Trash size={14} className="text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Templates below list if workflows exist */}
      {templates.length > 0 && workflows.length > 0 && (
        <div data-testid="workflow-templates-section">
          <h2 className="text-sm font-semibold text-[#1A2421] mb-3">Ajouter depuis un template</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {templates.map(tpl => (
              <button key={tpl.id} onClick={() => openFromTemplate(tpl)} className="text-left p-3 rounded-lg border border-[#E8E6E1] hover:bg-[#F9F8F5] transition-colors" data-testid={`tpl-btn-${tpl.id}`}>
                <p className="text-xs font-semibold text-[#1A2421] truncate">{tpl.name}</p>
                <p className="text-[10px] text-[#5E6D66] mt-0.5 truncate">{tpl.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Builder Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-workflow-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FlowArrow size={20} className="text-[#2D5A4C]" />
              {builderData?.id ? 'Modifier le workflow' : 'Nouveau workflow'}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Construisez votre automatisation visuellement : declencheur, conditions avancees, et actions.
            </DialogDescription>
          </DialogHeader>
          <WorkflowBuilder
            schema={schema}
            initialData={builderData}
            onSave={handleBuilderSave}
            onCancel={() => setCreateOpen(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Clock size={20} className="text-[#2D5A4C]" />
              Historique: {historyWfName}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Executions recentes de ce workflow
            </DialogDescription>
          </DialogHeader>
          {historyData.length === 0 ? (
            <p className="text-sm text-[#5E6D66] py-8 text-center">Aucune execution</p>
          ) : (
            <div className="space-y-2">
              {historyData.map(h => (
                <div key={h.id} className="border border-[#E8E6E1] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={h.success ? 'bg-emerald-100 text-emerald-700 text-[9px]' : 'bg-red-100 text-red-700 text-[9px]'}>
                      {h.success ? <CheckCircle size={10} className="mr-0.5" /> : <XCircle size={10} className="mr-0.5" />}
                      {h.success ? 'Succes' : 'Echec'}
                    </Badge>
                    <span className="text-[10px] text-[#5E6D66]">
                      {h.executed_at ? new Date(h.executed_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#5E6D66]">{h.entity_type} {h.entity_id?.slice(0, 8)}...</p>
                  {(h.actions_executed || []).map((ae, i) => (
                    <p key={i} className="text-[10px] text-[#1A2421] mt-0.5">{ae.success ? '✓' : '✗'} {ae.detail}</p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent Executions Dialog */}
      <Dialog open={showRecent} onOpenChange={setShowRecent}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto" data-testid="recent-executions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Clock size={20} className="text-[#2D5A4C]" />
              Executions recentes
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Toutes les executions de workflows recentes
            </DialogDescription>
          </DialogHeader>
          {recentExecs.length === 0 ? (
            <p className="text-sm text-[#5E6D66] py-8 text-center">Aucune execution recente</p>
          ) : (
            <div className="space-y-2">
              {recentExecs.map(h => (
                <div key={h.id} className="border border-[#E8E6E1] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#1A2421]">{h.workflow_name}</span>
                    <Badge className={h.success ? 'bg-emerald-100 text-emerald-700 text-[9px]' : 'bg-red-100 text-red-700 text-[9px]'}>
                      {h.success ? 'Succes' : 'Echec'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[#5E6D66]">
                    <span>{h.entity_type}</span>
                    <span>{h.executed_at ? new Date(h.executed_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
